import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import MobileHeader from './MobileHeader';
import RecibiLinkGenerator from './RecibiLinkGenerator';
import { KpiCard, EmailStatusBadge, DraftsList, DashboardHeader, DeleteDraftDialog, OnboardingStatusBadge } from './dashboard/index';
import { apiGet, apiDelete } from '../api/apiClient';
import { relativeTimeEs, isRecent } from '../utils/dateHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CHART_COLORS = ['#1a5c2e', '#2d8a4e', '#3aaa62', '#5ecc80', '#87dea3', '#b0efbf'];

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.24, ease: [0.2, 0.8, 0.2, 1] } }
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } }
};

export default function Dashboard() {
  const [kpis, setKpis] = useState(null);
  const [onboardings, setOnboardings] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('all');
  const [emailStatusFilter, setEmailStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [resendLoading, setResendLoading] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [linkGeneratorOpen, setLinkGeneratorOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [sendingDraft, setSendingDraft] = useState(null);
  const [deletingDraft, setDeletingDraft] = useState(null);
  const [draftToDelete, setDraftToDelete] = useState(null);

  // Filtrado del lado del cliente
  const filteredOnboardings = useMemo(() => {
    return onboardings.filter(item => {
      // Filtro de estado de email
      if (emailStatusFilter !== 'all') {
        const status = item.email_welcome_status;
        if (emailStatusFilter === 'sent' && status !== 'sent') return false;
        if (emailStatusFilter === 'pending' && status !== 'pending') return false;
        if (emailStatusFilter === 'error' && status !== 'error') return false;
      }
      
      // Filtro de fecha
      if (dateFilter !== 'all' && item.created_at) {
        const itemDate = new Date(item.created_at);
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        const monthAgo = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
        
        if (dateFilter === 'today' && itemDate < today) return false;
        if (dateFilter === 'week' && itemDate < weekAgo) return false;
        if (dateFilter === 'month' && itemDate < monthAgo) return false;
      }
      
      return true;
    });
  }, [onboardings, emailStatusFilter, dateFilter]);

  const fetchKPIs = useCallback(async () => {
    try {
      const data = await apiGet('/api/kpis');
      setKpis(data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchOnboardings = useCallback(async (p = 1, s = search, d = deptFilter) => {
    setTableLoading(true);
    try {
      const params = { page: p, limit: 10, status: 'sent' };
      if (s) params.search = s;
      if (d && d !== 'all') params.department = d;
      const data = await apiGet('/api/onboardings', params);
      setOnboardings(data.items || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch (e) { console.error(e); }
    finally { setTableLoading(false); }
  }, [search, deptFilter]);

  const fetchDrafts = useCallback(async () => {
    try {
      const data = await apiGet('/api/onboardings', { status: 'draft', limit: 100 });
      setDrafts(data.items || []);
    } catch (e) { console.error(e); }
  }, []);

  const handleSendDraft = async (draftId) => {
    setSendingDraft(draftId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/onboardings/${draftId}/send`, { method: 'PUT' });
      if (!res.ok) throw new Error('Error enviando borrador');
      toast.success('Borrador enviado correctamente');
      await Promise.all([fetchDrafts(), fetchOnboardings(page), fetchKPIs()]);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSendingDraft(null);
    }
  };

  // Abre el diálogo de confirmación de borrado
  const handleDeleteDraftRequest = useCallback((draft) => {
    setDraftToDelete(draft);
  }, []);

  // Ejecuta el borrado definitivo tras confirmación
  const handleDeleteDraftConfirm = useCallback(async (draftId) => {
    setDeletingDraft(draftId);
    try {
      await apiDelete(`/api/onboardings/${draftId}`);
      toast.success('Borrador eliminado correctamente');
      setDraftToDelete(null);
      await Promise.all([fetchDrafts(), fetchKPIs()]);
    } catch (e) {
      toast.error(e.message || 'No se pudo eliminar el borrador');
    } finally {
      setDeletingDraft(null);
    }
  }, [fetchDrafts, fetchKPIs]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchKPIs(), fetchOnboardings(1), fetchDrafts()]);
      setLoading(false);
    };
    init();
    fetch(`${BACKEND_URL}/api/departments`)
      .then(r => r.json())
      .then(d => setDepartments(d.departments || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount - functions are stable via useCallback

  useEffect(() => {
    setPage(1);
    fetchOnboardings(1, search, deptFilter);
  }, [search, deptFilter, fetchOnboardings]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchKPIs(), fetchOnboardings(page), fetchDrafts()]);
    setRefreshing(false);
    toast.success('Datos actualizados');
  };

  const handleExport = () => {
    window.open(`${BACKEND_URL}/api/onboardings/export/csv`, '_blank');
    toast.success('Descargando informe ISO CSV…');
  };

  const handleResend = async (id, emailType, employeeName) => {
    setResendLoading(id);
    try {
      const res = await fetch(`${BACKEND_URL}/api/onboardings/${id}/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email_type: emailType })
      });
      if (!res.ok) throw new Error();
      toast.success(`Correos reenviados a ${employeeName}`);
      await fetchOnboardings(page);
    } catch {
      toast.error('Error al reenviar correos');
    } finally {
      setResendLoading(null);
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    try {
      return new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return d; }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          {[1,2,3,4].map(i => (
            <div key={i} style={{ borderRadius: '12px', padding: '20px', border: '1px solid hsl(140 18% 86%)', height: '110px',
              background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e8e2 50%, #f0f0f0 75%)',
              backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite'
            }} />
          ))}
        </div>
        <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
      </div>
    );
  }

  return (
    <div className="page-container">
      

      {/* Page Header */}
      <DashboardHeader
        refreshing={refreshing}
        onRefresh={handleRefresh}
        onExport={handleExport}
      />

      {/* KPI CARDS */}
      <motion.div initial="hidden" animate="show" variants={stagger}
        style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' }}
      >
        <KpiCard icon="💾" label="Borradores" value={drafts.length} subtitle="Pendientes de enviar" testId="kpi-drafts" color="#f59e0b" />
        <KpiCard icon="🏆" label="Total Altas" value={kpis?.total ?? 0} subtitle="Desde el inicio" testId="kpi-total-altas" />
        <KpiCard icon="📅" label="Este mes" value={kpis?.this_month ?? 0} subtitle="Mes en curso" testId="kpi-month-altas" color="#2d8a4e" />
        <KpiCard icon="🗓" label="Esta semana" value={kpis?.this_week ?? 0} subtitle="Semana en curso" testId="kpi-week-altas" color="#3aaa62" />
      </motion.div>

      {/* DRAFTS SECTION */}
      <DraftsList
        drafts={drafts}
        sendingDraft={sendingDraft}
        deletingDraft={deletingDraft}
        onSendDraft={handleSendDraft}
        onDeleteDraft={handleDeleteDraftRequest}
      />

      <DeleteDraftDialog
        open={!!draftToDelete}
        onOpenChange={(v) => !v && setDraftToDelete(null)}
        draft={draftToDelete}
        deleting={!!deletingDraft}
        onConfirm={handleDeleteDraftConfirm}
      />

      {/* CHARTS */}
      <motion.div initial="hidden" animate="show" variants={stagger}
        style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: '20px', marginBottom: '24px' }}
      >
        {/* Area Chart: Daily Trend */}
        <motion.div variants={fadeUp}>
          <Card style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
            <CardHeader style={{ paddingBottom: '8px' }}>
              <CardTitle style={{ fontSize: '15px', color: '#1a5c2e' }}>Altas últimos 14 días</CardTitle>
              <CardDescription>Tendencia diaria de nuevas incorporaciones</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={kpis?.daily_trend || []} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
                  <defs>
                    <linearGradient id="gradGreen" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#1a5c2e" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="#1a5c2e" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(140 18% 90%)" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#888' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#888' }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ background: '#fff', border: '1px solid #cfe3d6', borderRadius: '8px', fontSize: '13px' }}
                    labelStyle={{ color: '#1a5c2e', fontWeight: 600 }}
                  />
                  <Area type="monotone" dataKey="altas" stroke="#1a5c2e" strokeWidth={2} fill="url(#gradGreen)" dot={false} activeDot={{ r: 5, fill: '#1a5c2e' }} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </motion.div>

        {/* Bar Chart: Department Breakdown */}
        <motion.div variants={fadeUp}>
          <Card style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
            <CardHeader style={{ paddingBottom: '8px' }}>
              <CardTitle style={{ fontSize: '15px', color: '#1a5c2e' }}>Altas por lugar de trabajo</CardTitle>
              <CardDescription>Distribución de empleados por ubicación</CardDescription>
            </CardHeader>
            <CardContent>
              {kpis?.department_breakdown?.length > 0 ? (
                <ResponsiveContainer width="100%" height={Math.max(300, kpis.department_breakdown.length * 25)}>
                  <BarChart data={kpis.department_breakdown} layout="vertical" margin={{ top: 4, right: 8, bottom: 0, left: 120 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(140 18% 90%)" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: '#888' }} allowDecimals={false} />
                    <YAxis 
                      type="category" 
                      dataKey="name" 
                      tick={{ fontSize: 10, fill: '#555' }} 
                      width={110}
                      tickFormatter={(value) => value.length > 18 ? value.substring(0, 18) + '...' : value}
                    />
                    <Tooltip
                      contentStyle={{ background: '#fff', border: '1px solid #cfe3d6', borderRadius: '8px', fontSize: '13px' }}
                      formatter={(value, name, props) => [value, props.payload.name]}
                    />
                    <Bar dataKey="value" fill="#2d8a4e" radius={[0, 4, 4, 0]}>
                      {kpis.department_breakdown.map((entry, idx) => (
                        <Cell key={`cell-${entry.name}-${idx}`} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb', fontSize: '14px' }}>
                  Sin datos aún
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

      </motion.div>

      {/* EXTRA STATS ROW */}
      <motion.div initial="hidden" animate="show" variants={stagger}
        style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}
        className="stats-row"
      >
        {[
          { label: 'Enviados', val: kpis?.email_stats?.sent ?? 0, color: '#1a5c2e', bg: '#f0faf3', border: '#cfe3d6' },
          { label: 'En cola', val: kpis?.email_stats?.queued ?? 0, color: '#1565c0', bg: '#e8f4fd', border: '#90caf9' },
          { label: 'Con error', val: kpis?.email_stats?.error ?? 0, color: '#c62828', bg: '#fdecea', border: '#ef9a9a' },
        ].map((s, i) => (
          <motion.div key={`email-stat-${s.label}`} variants={fadeUp} style={{
            background: s.bg, border: `1px solid ${s.border}`, borderRadius: '10px',
            padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between'
          }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: s.color }}>{s.label}</span>
            <span style={{ fontSize: '24px', fontFamily: 'Space Grotesk', fontWeight: 700, color: s.color }}>{s.val}</span>
          </motion.div>
        ))}
      </motion.div>

      {/* TABLE */}
      <motion.div initial="hidden" animate="show" variants={fadeUp}>
        <Card style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
          <CardHeader style={{ paddingBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <CardTitle style={{ fontSize: '16px', color: '#1a5c2e' }}>Registro de Altas</CardTitle>
                <CardDescription>{total} empleado{total !== 1 ? 's' : ''} registrado{total !== 1 ? 's' : ''}</CardDescription>
              </div>
            </div>
            <Separator style={{ marginTop: '12px', marginBottom: '12px' }} />
            {/* Filters */}
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
              <Input
                data-testid="dashboard-search-input"
                placeholder="🔍 Buscar por nombre, correo…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ height: '38px', maxWidth: '280px', fontSize: '13px' }}
              />
              <Select value={deptFilter} onValueChange={setDeptFilter}>
                <SelectTrigger style={{ height: '38px', width: '200px', fontSize: '13px' }}>
                  <SelectValue placeholder="Lugar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los lugares</SelectItem>
                  {departments.map(d => (
                    <SelectItem key={d} value={d}>{d.substring(0, 40)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={emailStatusFilter} onValueChange={setEmailStatusFilter}>
                <SelectTrigger style={{ height: '38px', width: '160px', fontSize: '13px' }}>
                  <SelectValue placeholder="Estado email" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los emails</SelectItem>
                  <SelectItem value="sent">Enviados</SelectItem>
                  <SelectItem value="pending">Pendientes</SelectItem>
                  <SelectItem value="error">Con error</SelectItem>
                </SelectContent>
              </Select>
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger style={{ height: '38px', width: '160px', fontSize: '13px' }}>
                  <SelectValue placeholder="Fecha" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las fechas</SelectItem>
                  <SelectItem value="today">Hoy</SelectItem>
                  <SelectItem value="week">Esta semana</SelectItem>
                  <SelectItem value="month">Este mes</SelectItem>
                </SelectContent>
              </Select>
              {(search || deptFilter !== 'all' || emailStatusFilter !== 'all' || dateFilter !== 'all') && (
                <Button
                  variant="outline"
                  onClick={() => { 
                    setSearch(''); 
                    setDeptFilter('all'); 
                    setEmailStatusFilter('all');
                    setDateFilter('all');
                  }}
                  style={{ height: '38px', fontSize: '13px' }}
                >
                  × Limpiar
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent style={{ padding: '0' }}>
            {tableLoading ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>⏳</div>
                <p style={{ fontSize: '14px' }}>Cargando datos…</p>
              </div>
            ) : onboardings.length === 0 ? (
              <div style={{ padding: '60px', textAlign: 'center', color: '#bbb' }}>
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>👤</div>
                <p style={{ fontSize: '15px', color: '#888', marginBottom: '8px' }}>No hay altas que coincidan con los filtros.</p>
                {(search || deptFilter !== 'all') && (
                  <Button variant="outline" onClick={() => { setSearch(''); setDeptFilter('all'); }} style={{ marginTop: '8px', fontSize: '13px' }}>Limpiar filtros</Button>
                )}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid hsl(140 18% 88%)' }}>
                      {['Empleado', 'Estado', 'Enviado', 'Lugar de Trabajo', 'Incorporación', 'Email Bienvenida', 'Email Contraseña', 'Políticas IT', 'Acciones'].map(h => (
                        <th key={h} style={{
                          padding: '12px 16px', textAlign: 'left', fontSize: '12px',
                          fontWeight: 700, color: '#1a5c2e', textTransform: 'uppercase',
                          letterSpacing: '0.5px', background: '#f9fdf9'
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOnboardings.map((item, idx) => (
                      <tr
                        key={item.id || idx}
                        data-testid="onboarding-row"
                        style={{
                          borderBottom: '1px solid hsl(140 18% 92%)',
                          transition: 'background-color 0.15s',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'hsl(145 40% 97%)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        onClick={() => setSelectedRecord(item)}
                      >
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ fontWeight: 600, fontSize: '14px', color: '#1a1a1a' }}>{item.nombre}</div>
                          <div style={{ fontSize: '12px', color: '#888' }}>{item.correo_corporativo}</div>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <OnboardingStatusBadge status={item.status || 'sent'} />
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '13px', color: '#555' }}>
                          {item.sent_at ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              <span
                                data-testid="onboarding-sent-relative"
                                style={{ fontWeight: 500, color: isRecent(item.sent_at) ? '#1a5c2e' : '#555' }}
                              >
                                {relativeTimeEs(item.sent_at)}
                              </span>
                              {isRecent(item.sent_at) && (
                                <span style={{
                                  fontSize: '10px',
                                  color: '#1a5c2e',
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.4px'
                                }}>
                                  • Reciente
                                </span>
                              )}
                            </div>
                          ) : (
                            <span style={{ color: '#aaa', fontSize: '12px' }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{
                            background: 'hsl(145 40% 90%)', color: '#1a5c2e',
                            borderRadius: '6px', padding: '3px 8px', fontSize: '12px', fontWeight: 500
                          }}>{item.lugar_trabajo || item.departamento}</span>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '13px', color: '#555' }}>
                          {formatDate(item.fecha_incorporacion)}
                        </td>
                        <td style={{ padding: '12px 16px' }}><EmailStatusBadge status={item.email_welcome_status} /></td>
                        <td style={{ padding: '12px 16px' }}><EmailStatusBadge status={item.email_password_status} /></td>
                        <td style={{ padding: '12px 16px' }}>
                          {item.politicas_aceptadas_at ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              <span style={{ fontSize: '12px', color: '#16a34a', fontWeight: 700 }}>✅ Aceptadas</span>
                              <span style={{ fontSize: '11px', color: '#4b7a5c' }}>
                                {new Date(item.politicas_aceptadas_at).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                                {' '}
                                {new Date(item.politicas_aceptadas_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                          ) : (
                            <span style={{ fontSize: '12px', color: '#f59e0b', fontWeight: 600 }}>⏳ Pendiente</span>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px' }} onClick={e => e.stopPropagation()}>
                          <Button
                            data-testid="onboarding-row-resend-email-button"
                            variant="outline"
                            disabled={resendLoading === item.id}
                            onClick={() => handleResend(item.id, 'both', item.nombre)}
                            style={{ fontSize: '12px', height: '32px', padding: '0 12px', color: '#1a5c2e', borderColor: '#cfe3d6' }}
                          >
                            {resendLoading === item.id ? '⏳' : '🔄'} Reenviar
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>

          {/* Pagination */}
          {pages > 1 && (
            <div style={{ padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid hsl(140 18% 90%)' }}>
              <span style={{ fontSize: '13px', color: '#888' }}>Página {page} de {pages} &middot; {total} registros</span>
              <div style={{ display: 'flex', gap: '6px' }}>
                <Button
                  variant="outline" size="sm" style={{ height: '32px', fontSize: '12px' }}
                  disabled={page === 1}
                  onClick={() => { const np = page - 1; setPage(np); fetchOnboardings(np); }}
                >
                  ← Anterior
                </Button>
                {Array.from({ length: Math.min(5, pages) }, (_, i) => {
                  const p = Math.max(1, page - 2) + i;
                  if (p > pages) return null;
                  return (
                    <Button
                      key={p} size="sm"
                      style={{ height: '32px', fontSize: '12px', minWidth: '32px',
                        background: p === page ? '#1a5c2e' : 'transparent',
                        color: p === page ? '#fff' : '#555',
                        border: p === page ? 'none' : '1px solid hsl(140 18% 86%)' }}
                      onClick={() => { setPage(p); fetchOnboardings(p); }}
                    >{p}</Button>
                  );
                })}
                <Button
                  variant="outline" size="sm" style={{ height: '32px', fontSize: '12px' }}
                  disabled={page === pages}
                  onClick={() => { const np = page + 1; setPage(np); fetchOnboardings(np); }}
                >
                  Siguiente →
                </Button>
              </div>
            </div>
          )}
        </Card>
      </motion.div>

      {/* Record Detail Modal */}
      <Dialog open={!!selectedRecord} onOpenChange={() => setSelectedRecord(null)}>
        <DialogContent style={{ maxWidth: '500px' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Space Grotesk', color: '#1a5c2e' }}>
              {selectedRecord?.nombre}
            </DialogTitle>
            <DialogDescription>Detalle del alta de empleado</DialogDescription>
          </DialogHeader>
          {selectedRecord && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', background: '#f0f8f2', borderRadius: '10px', padding: '16px' }}>
                <DetailItem label="Nombre" value={selectedRecord.nombre} />
                <DetailItem label="Correo personal" value={selectedRecord.correo_personal} />
                <DetailItem label="Correo corporativo" value={selectedRecord.correo_corporativo} />
                <DetailItem label="Teléfono" value={selectedRecord.telefono} />
                <DetailItem label="Lugar de Trabajo" value={selectedRecord.lugar_trabajo || selectedRecord.departamento} />
                <DetailItem label="Incorporación" value={formatDate(selectedRecord.fecha_incorporacion)} />
                <DetailItem label="Fecha alta" value={formatDate(selectedRecord.created_at)} />
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <div style={{ flex: 1, background: '#f8f9fa', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                  <p style={{ fontSize: '11px', color: '#999', marginBottom: '4px' }}>Email Bienvenida</p>
                  <EmailStatusBadge status={selectedRecord.email_welcome_status} />
                </div>
                <div style={{ flex: 1, background: '#f8f9fa', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                  <p style={{ fontSize: '11px', color: '#999', marginBottom: '4px' }}>Email Contraseña</p>
                  <EmailStatusBadge status={selectedRecord.email_password_status} />
                </div>
              </div>
              {/* Trazabilidad de políticas IT */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{
                  background: selectedRecord.politicas_aceptadas_at ? '#f0faf3' : '#fffbeb',
                  border: `1px solid ${selectedRecord.politicas_aceptadas_at ? '#cfe3d6' : '#fde68a'}`,
                  borderRadius: '10px', padding: '14px 16px'
                }}>
                  <p style={{ fontSize: '12px', fontWeight: 700, color: selectedRecord.politicas_aceptadas_at ? '#15803d' : '#92400e', margin: '0 0 4px 0' }}>
                    {selectedRecord.politicas_aceptadas_at ? '✅ Políticas IT aceptadas (página de activación)' : '⏳ Políticas IT — pendiente desde enlace de activación'}
                  </p>
                  {selectedRecord.politicas_aceptadas_at ? (
                    <div>
                      <p style={{ fontSize: '12px', color: '#2d5c3d', margin: '0 0 2px 0' }}>
                        <strong>Fecha:</strong> {new Date(selectedRecord.politicas_aceptadas_at).toLocaleString('es-ES', { dateStyle: 'long', timeStyle: 'medium' })}
                      </p>
                      {selectedRecord.politicas_aceptadas_ip && (
                        <p style={{ fontSize: '11px', color: '#4b7a5c', margin: 0, fontStyle: 'italic' }}>IP: {selectedRecord.politicas_aceptadas_ip}</p>
                      )}
                    </div>
                  ) : (
                    <p style={{ fontSize: '12px', color: '#78716c', margin: 0 }}>El empleado aún no ha marcado el checkbox en el enlace de activación.</p>
                  )}
                </div>
                <div style={{
                  background: selectedRecord.politicas_qr_leidas_at ? '#f0faf3' : '#f5f3ff',
                  border: `1px solid ${selectedRecord.politicas_qr_leidas_at ? '#cfe3d6' : '#ddd6fe'}`,
                  borderRadius: '10px', padding: '14px 16px'
                }}>
                  <p style={{ fontSize: '12px', fontWeight: 700, color: selectedRecord.politicas_qr_leidas_at ? '#15803d' : '#5b21b6', margin: '0 0 4px 0' }}>
                    {selectedRecord.politicas_qr_leidas_at ? '✅ Guía de Buenas Prácticas confirmada (QR/email)' : '⏳ Guía de Buenas Prácticas — pendiente de confirmación vía email'}
                  </p>
                  {selectedRecord.politicas_qr_leidas_at ? (
                    <div>
                      <p style={{ fontSize: '12px', color: '#2d5c3d', margin: '0 0 2px 0' }}>
                        <strong>Fecha:</strong> {new Date(selectedRecord.politicas_qr_leidas_at).toLocaleString('es-ES', { dateStyle: 'long', timeStyle: 'medium' })}
                      </p>
                      {selectedRecord.politicas_qr_leidas_ip && (
                        <p style={{ fontSize: '11px', color: '#4b7a5c', margin: 0, fontStyle: 'italic' }}>IP: {selectedRecord.politicas_qr_leidas_ip}</p>
                      )}
                    </div>
                  ) : (
                    <p style={{ fontSize: '12px', color: '#78716c', margin: 0 }}>El empleado aún no ha confirmado la lectura desde el email.</p>
                  )}
                </div>
              </div>
              <Button
                onClick={() => handleResend(selectedRecord.id, 'both', selectedRecord.nombre)}
                style={{ background: '#1a5c2e', color: '#fff' }}
                disabled={resendLoading === selectedRecord.id}
              >
                {resendLoading === selectedRecord.id ? 'Reenviando...' : '🔄 Reenviar ambos correos'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <style>{`
        @media (max-width: 640px) {
          .stats-row { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* Powered by footer */}
      <div style={{ paddingBottom: '24px', textAlign: 'center' }}>
        <p style={{ fontSize: '11px', color: '#bbb', fontStyle: 'italic' }}>Powered by Valoriza&apos;s Innovation Team · © 2026</p>
      </div>

      {/* Floating Action Button - Generate Recibi Link */}
      <Button
        onClick={() => {
          setSelectedEmployee(null);
          setLinkGeneratorOpen(true);
        }}
        data-testid="fab-generate-recibi-link"
        style={{
          position: 'fixed',
          bottom: '32px',
          right: '32px',
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #2d8a4e 0%, #1a5c2e 100%)',
          color: '#fff',
          boxShadow: '0 8px 24px rgba(26, 92, 46, 0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '28px',
          zIndex: 1000,
          border: 'none',
          cursor: 'pointer',
          transition: 'all 0.3s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.1) rotate(15deg)';
          e.currentTarget.style.boxShadow = '0 12px 32px rgba(26, 92, 46, 0.5)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(26, 92, 46, 0.4)';
        }}
        title="Generar Link de Recibí"
      >
        📦
      </Button>

      {/* Recibi Link Generator Modal */}
      <RecibiLinkGenerator
        isOpen={linkGeneratorOpen}
        onClose={() => {
          setLinkGeneratorOpen(false);
          setSelectedEmployee(null);
        }}
        employeeData={selectedEmployee}
      />
    </div>
  );
}

function DetailItem({ label, value }) {
  return (
    <div>
      <p style={{ fontSize: '11px', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px', margin: 0 }}>{label}</p>
      <p style={{ fontSize: '13px', fontWeight: 600, color: '#1a1a1a', margin: '2px 0 0', wordBreak: 'break-all' }}>{value || '-'}</p>
    </div>
  );
}
